<template>
  <div class="archive-page">
    <header class="archive-toolbar">
      <button class="toolbar-button" type="button" @click="goBack">
        <el-icon><ArrowLeft /></el-icon>
        <span>机器人中心</span>
      </button>
      <div class="toolbar-title">
        <el-icon><Document /></el-icon>
        <strong>机器人档案</strong>
        <span v-if="robot">{{ robot.archiveNo || `RBT-${robot.id}` }}</span>
      </div>
      <div class="toolbar-actions">
        <button class="icon-button" type="button" title="刷新档案" @click="loadArchive" :disabled="loading">
          <el-icon><Refresh /></el-icon>
        </button>
        <button v-if="!editing" class="primary-action" type="button" @click="beginEdit" :disabled="!robot">
          <el-icon><EditPen /></el-icon>
          <span>编辑档案</span>
        </button>
        <template v-else>
          <button class="secondary-action" type="button" @click="cancelEdit" :disabled="saving">
            <el-icon><Close /></el-icon>
            <span>取消</span>
          </button>
          <button class="primary-action" type="button" @click="saveArchive" :disabled="saving">
            <el-icon><Check /></el-icon>
            <span>{{ saving ? '保存中' : '保存' }}</span>
          </button>
        </template>
      </div>
    </header>

    <main class="archive-main" v-loading="loading">
      <el-empty v-if="!loading && !robot" description="未找到机器人档案">
        <el-button @click="goBack">返回机器人中心</el-button>
      </el-empty>

      <template v-if="robot">
        <section class="identity-band">
          <div class="robot-portrait">
            <img v-if="robot.coverImageUrl" :src="robot.coverImageUrl" :alt="robot.name" />
            <div v-else class="portrait-placeholder">R</div>
            <span :class="['online-indicator', robot.sdkAgentOnline ? 'online' : 'offline']"></span>
          </div>

          <div class="identity-copy">
            <div class="record-line">
              <span>{{ categoryLabel(robot.category) }}</span>
              <span>{{ registerStatusLabel(robot.registerStatus) }}</span>
              <span>{{ robot.acceptingOrders !== 0 ? '接单中' : '暂停接单' }}</span>
            </div>
            <h1>{{ robot.name }}</h1>
            <p>{{ identityLine }}</p>
            <div class="identity-meta">
              <span><el-icon><Location /></el-icon>{{ robot.serviceRegionLimit || '服务区域未登记' }}</span>
              <span><el-icon><Calendar /></el-icon>{{ formatDate(robot.createTime) }} 建档</span>
              <span><el-icon><Monitor /></el-icon>{{ robot.sdkAgentOnline ? 'SDK Agent 在线' : 'SDK Agent 离线' }}</span>
            </div>
          </div>

          <div class="archive-stamp">
            <small>ROBOT FILE</small>
            <strong>{{ robot.archiveNo || `RBT-${String(robot.id).padStart(6, '0')}` }}</strong>
            <span>{{ completeness }}% 完整</span>
          </div>
        </section>

        <section class="metrics-band" aria-label="机器人履历统计">
          <article class="metric-item">
            <span>累计接单</span>
            <strong>{{ statistics.totalOrders || 0 }}</strong>
            <small>全部任务记录</small>
          </article>
          <article class="metric-item completed">
            <span>完成率</span>
            <strong>{{ number(statistics.completionRate) }}<em>%</em></strong>
            <small>{{ statistics.completedOrders || 0 }} 单已完成</small>
          </article>
          <article class="metric-item active">
            <span>进行中</span>
            <strong>{{ statistics.activeOrders || 0 }}</strong>
            <small>{{ robot.acceptingOrders !== 0 ? '当前开放接单' : '当前暂停接单' }}</small>
          </article>
          <article class="metric-item income">
            <span>累计净收益</span>
            <strong>{{ coin(statistics.netIncomeRobocoin) }}</strong>
            <small>RoboCoin</small>
          </article>
          <article class="metric-item score">
            <span>档案评分</span>
            <strong>{{ statistics.score || robot.score || 0 }}</strong>
            <small>平台记录</small>
          </article>
        </section>

        <section class="archive-sheet">
          <div class="sheet-column identity-column">
            <div class="section-heading">
              <div>
                <span>IDENTITY</span>
                <h2>身份资料</h2>
              </div>
              <em>{{ robot.archiveNo }}</em>
            </div>

            <el-form v-if="editing" class="archive-form" label-position="top" :model="form">
              <div class="form-grid">
                <el-form-item label="机器人名称" class="full-row">
                  <el-input v-model.trim="form.name" maxlength="100" />
                </el-form-item>
                <el-form-item label="制造商 / 品牌">
                  <el-input v-model.trim="form.manufacturer" maxlength="100" placeholder="例如：Unitree" />
                </el-form-item>
                <el-form-item label="型号">
                  <el-input v-model.trim="form.model" maxlength="100" placeholder="例如：Go2 EDU" />
                </el-form-item>
                <el-form-item label="设备序列号" class="full-row">
                  <el-input v-model.trim="form.serialNumber" maxlength="128" />
                </el-form-item>
                <el-form-item label="生产日期">
                  <el-date-picker v-model="form.manufactureDate" type="date" value-format="YYYY-MM-DD" placeholder="选择日期" />
                </el-form-item>
                <el-form-item label="投入使用日期">
                  <el-date-picker v-model="form.commissionedDate" type="date" value-format="YYYY-MM-DD" placeholder="选择日期" />
                </el-form-item>
                <el-form-item label="联系方式">
                  <el-input v-model.trim="form.contactPhone" maxlength="50" />
                </el-form-item>
                <el-form-item label="收款钱包地址（接单前必填）" class="full-row">
                  <el-input
                    ref="walletInput"
                    v-model.trim="form.walAddress"
                    maxlength="42"
                    placeholder="例如：0xFaA0d9A670b6CC505EeC57046edD2FdB7A03AB55"
                  />
                  <small class="field-hint">可暂时留空；开启接单前必须填写有效的 EVM 钱包地址。</small>
                </el-form-item>
                <el-form-item label="服务区域">
                  <el-input v-model.trim="form.serviceRegionLimit" maxlength="255" />
                </el-form-item>
                <el-form-item label="能力说明" class="full-row">
                  <el-input v-model.trim="form.capabilityDesc" type="textarea" :rows="3" maxlength="1000" show-word-limit />
                </el-form-item>
                <el-form-item label="档案备注" class="full-row">
                  <el-input v-model.trim="form.archiveNotes" type="textarea" :rows="3" maxlength="1000" show-word-limit />
                </el-form-item>
              </div>
            </el-form>

            <dl v-else class="record-grid">
              <div>
                <dt>制造商 / 品牌</dt>
                <dd>{{ robot.manufacturer || '未登记' }}</dd>
              </div>
              <div>
                <dt>型号</dt>
                <dd>{{ robot.model || '未登记' }}</dd>
              </div>
              <div>
                <dt>设备序列号</dt>
                <dd>{{ robot.serialNumber || '未登记' }}</dd>
              </div>
              <div>
                <dt>机器人类别</dt>
                <dd>{{ categoryLabel(robot.category) }}</dd>
              </div>
              <div>
                <dt>生产日期</dt>
                <dd>{{ formatDate(robot.manufactureDate) }}</dd>
              </div>
              <div>
                <dt>投入使用日期</dt>
                <dd>{{ formatDate(robot.commissionedDate) }}</dd>
              </div>
              <div>
                <dt>联系方式</dt>
                <dd>{{ robot.contactPhone || '未登记' }}</dd>
              </div>
              <div>
                <dt>收款钱包</dt>
                <dd class="mono">{{ shortenWallet(robot.walAddress) }}</dd>
              </div>
              <div class="wide-record">
                <dt>服务区域</dt>
                <dd>{{ robot.serviceRegionLimit || '未限制服务区域' }}</dd>
              </div>
            </dl>
          </div>

          <div class="sheet-column device-column">
            <div class="section-heading">
              <div>
                <span>DEVICE</span>
                <h2>设备与能力</h2>
              </div>
              <el-icon><Cpu /></el-icon>
            </div>

            <div class="device-status-row">
              <span :class="['status-dot', robot.sdkAgentOnline ? 'online' : 'offline']"></span>
              <div>
                <strong>{{ robot.sdkAgentOnline ? 'SDK Agent 正常在线' : 'SDK Agent 当前离线' }}</strong>
                <small>{{ heartbeatLabel }}</small>
              </div>
            </div>

            <dl class="device-grid" v-if="device">
              <div><dt>设备型号</dt><dd>{{ device.deviceModel || robot.model || '未上报' }}</dd></div>
              <div><dt>工控机</dt><dd>{{ device.computerName || '未上报' }}</dd></div>
              <div><dt>处理器</dt><dd>{{ device.cpuModel || '未上报' }}</dd></div>
              <div><dt>SDK 版本</dt><dd>{{ device.sdkVersion || robot.sdkLastHeartbeatSdkVersion || '未上报' }}</dd></div>
              <div><dt>身份类型</dt><dd>{{ device.platformIdentityType || '未上报' }}</dd></div>
              <div><dt>Go2 SDK</dt><dd>{{ device.go2SdkDetected ? '已检测' : '未检测' }}</dd></div>
              <div><dt>DDS 网卡</dt><dd>{{ device.go2NetworkInterface || '未上报' }}</dd></div>
              <div><dt>最近上报</dt><dd>{{ formatDateTime(device.receivedAt) }}</dd></div>
            </dl>
            <el-empty v-else :image-size="54" description="暂无 SDK 设备上报" />

            <div class="text-record">
              <span>能力说明</span>
              <p>{{ robot.capabilityDesc || '暂无能力说明。' }}</p>
            </div>
            <div class="text-record note-record">
              <span>档案备注</span>
              <p>{{ robot.archiveNotes || '暂无档案备注。' }}</p>
            </div>
          </div>
        </section>

        <section class="history-band">
          <div class="history-heading">
            <div>
              <span>ORDER HISTORY</span>
              <h2>接单履历</h2>
            </div>
            <p>服务时长 {{ durationLabel(statistics.serviceMinutes) }} · 取消 {{ statistics.cancelledOrders || 0 }} 单 · 仲裁 {{ statistics.arbitrationOrders || 0 }} 单</p>
          </div>

          <el-table v-if="recentOrders.length" class="history-table" :data="recentOrders" stripe>
            <el-table-column label="订单" min-width="210">
              <template #default="scope">
                <div class="order-title">
                  <strong>{{ scope.row.taskTitle || `任务 #${scope.row.taskId}` }}</strong>
                  <small>#{{ scope.row.id }} · {{ serviceTypeLabel(scope.row.serviceType) }}</small>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="状态" width="110">
              <template #default="scope">
                <span :class="['order-status', orderStatusClass(scope.row.orderStatus)]">{{ orderStatusLabel(scope.row.orderStatus) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="服务地点" min-width="220">
              <template #default="scope">{{ orderLocation(scope.row) }}</template>
            </el-table-column>
            <el-table-column label="服务时长" width="110">
              <template #default="scope">{{ durationLabel(scope.row.actualServiceMinutes) }}</template>
            </el-table-column>
            <el-table-column label="机器人收益" width="130" align="right">
              <template #default="scope">{{ coin(scope.row.providerIncomeRobocoin || scope.row.finalRobocoin) }}</template>
            </el-table-column>
            <el-table-column label="更新时间" width="170">
              <template #default="scope">{{ formatDateTime(scope.row.updateTime) }}</template>
            </el-table-column>
          </el-table>

          <div v-if="recentOrders.length" class="mobile-history">
            <article v-for="order in recentOrders" :key="order.id">
              <div>
                <strong>{{ order.taskTitle || `任务 #${order.taskId}` }}</strong>
                <span :class="['order-status', orderStatusClass(order.orderStatus)]">{{ orderStatusLabel(order.orderStatus) }}</span>
              </div>
              <p>{{ orderLocation(order) }}</p>
              <small>#{{ order.id }} · {{ formatDateTime(order.updateTime) }} · {{ coin(order.providerIncomeRobocoin || order.finalRobocoin) }} ROC</small>
            </article>
          </div>

          <el-empty v-if="!recentOrders.length" :image-size="72" description="这台机器人还没有接单记录" />
        </section>
      </template>
    </main>
  </div>
</template>

<script>
import {
  ArrowLeft,
  Calendar,
  Check,
  Close,
  Cpu,
  Document,
  EditPen,
  Location,
  Monitor,
  Refresh
} from '@element-plus/icons-vue'
import { getRobotArchive, updateRobotArchive } from '../api/robot-register'
import { getApiErrorMessage } from '../api/http'
import { getAuthUser } from '../utils/auth'

export default {
  name: 'RobotArchivePage',
  components: { ArrowLeft, Calendar, Check, Close, Cpu, Document, EditPen, Location, Monitor, Refresh },
  data() {
    return {
      currentUser: getAuthUser(),
      loading: false,
      saving: false,
      editing: false,
      focusWalletOnLoad: this.$route.query.focus === 'wallet',
      robot: null,
      device: null,
      recentOrders: [],
      statistics: {},
      form: this.emptyForm()
    }
  },
  computed: {
    robotId() {
      return Number(this.$route.params.robotId)
    },
    identityLine() {
      const parts = [this.robot.manufacturer, this.robot.model, this.robot.serialNumber]
        .filter(Boolean)
      return parts.length ? parts.join(' · ') : '制造商、型号和序列号待补充'
    },
    completeness() {
      const fields = ['manufacturer', 'model', 'serialNumber', 'manufactureDate', 'commissionedDate', 'serviceRegionLimit', 'capabilityDesc']
      const completed = fields.filter(key => this.robot && this.robot[key]).length
      return Math.round((completed / fields.length) * 100)
    },
    heartbeatLabel() {
      if (!this.robot.sdkLastHeartbeatAt) return '暂无心跳记录'
      const age = Number(this.robot.sdkHeartbeatAgeSeconds)
      if (Number.isFinite(age)) {
        if (age < 60) return `${Math.max(0, Math.floor(age))} 秒前收到心跳`
        if (age < 3600) return `${Math.floor(age / 60)} 分钟前收到心跳`
        return `${Math.floor(age / 3600)} 小时前收到心跳`
      }
      return `最近心跳 ${this.formatDateTime(this.robot.sdkLastHeartbeatAt)}`
    }
  },
  created() {
    this.loadArchive()
  },
  methods: {
    emptyForm() {
      return {
        name: '',
        manufacturer: '',
        model: '',
        serialNumber: '',
        manufactureDate: '',
        commissionedDate: '',
        contactPhone: '',
        walAddress: '',
        serviceRegionLimit: '',
        capabilityDesc: '',
        archiveNotes: ''
      }
    },
    async loadArchive() {
      if (!this.currentUser || !this.currentUser.id || !this.robotId) {
        this.robot = null
        return
      }
      this.loading = true
      try {
        const response = await getRobotArchive(this.robotId, this.currentUser.id)
        if (response.data.code !== 200) {
          this.robot = null
          this.$message.error(response.data.msg || '机器人档案加载失败')
          return
        }
        const data = response.data.data || {}
        this.robot = data.robot || null
        this.statistics = data.statistics || {}
        this.recentOrders = data.recentOrders || []
        this.device = data.device || null
        if (this.robot) {
          this.form = this.formFromRobot(this.robot)
          if (this.focusWalletOnLoad) {
            this.focusWalletOnLoad = false
            this.editing = true
            this.$nextTick(() => this.$refs.walletInput && this.$refs.walletInput.focus())
          }
        }
      } catch (error) {
        this.robot = null
        this.$message.error(getApiErrorMessage(error, '机器人档案加载失败，请稍后重试。'))
      } finally {
        this.loading = false
      }
    },
    beginEdit() {
      this.form = this.formFromRobot(this.robot)
      this.editing = true
    },
    cancelEdit() {
      this.form = this.formFromRobot(this.robot)
      this.editing = false
    },
    async saveArchive() {
      if (!this.form.name) {
        this.$message.warning('机器人名称不能为空')
        return
      }
      if (this.form.manufactureDate && this.form.commissionedDate && this.form.commissionedDate < this.form.manufactureDate) {
        this.$message.warning('投入使用日期不能早于生产日期')
        return
      }
      if (this.form.walAddress && !this.isWalletAddress(this.form.walAddress)) {
        this.$message.warning('收款钱包格式不正确，应为 0x 开头的 42 位地址')
        return
      }
      this.saving = true
      try {
        const response = await updateRobotArchive(this.robotId, {
          userId: this.currentUser.id,
          ...this.form
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '档案保存失败')
          return
        }
        this.editing = false
        this.$message.success('机器人档案已更新')
        await this.loadArchive()
      } catch (error) {
        this.$message.error(getApiErrorMessage(error, '档案保存失败，请稍后重试。'))
      } finally {
        this.saving = false
      }
    },
    formFromRobot(robot) {
      return {
        name: robot.name || '',
        manufacturer: robot.manufacturer || '',
        model: robot.model || '',
        serialNumber: robot.serialNumber || '',
        manufactureDate: this.inputDate(robot.manufactureDate),
        commissionedDate: this.inputDate(robot.commissionedDate),
        contactPhone: robot.contactPhone || '',
        walAddress: robot.walAddress || '',
        serviceRegionLimit: robot.serviceRegionLimit || '',
        capabilityDesc: robot.capabilityDesc || '',
        archiveNotes: robot.archiveNotes || ''
      }
    },
    goBack() {
      this.$router.push({ name: 'user' })
    },
    isWalletAddress(value) {
      return /^0x[a-fA-F0-9]{40}$/.test(String(value || '').replace(/\s+/g, ''))
    },
    categoryLabel(value) {
      const labels = {
        WHEELED: '轮式机器人', CLEANING: '清洁机器人', DRONE: '无人机',
        SHUTTLE: '接驳机器人', INSPECTION: '巡检机器人', GO2: '机器狗 Go2'
      }
      return labels[String(value || '').toUpperCase()] || value || '未分类'
    },
    registerStatusLabel(value) {
      const labels = { APPROVED: '已认证', PENDING_REVIEW: '待认证', REJECTED: '未通过', CANCELLED: '已注销' }
      return labels[String(value || '').toUpperCase()] || value || '未知状态'
    },
    serviceTypeLabel(value) {
      const labels = { HOME_SERVICE: '上门服务', SHUTTLE: '接驳任务', DELIVERY: '配送', INSPECTION: '巡检', PATROL: '巡逻' }
      return labels[String(value || '').toUpperCase()] || value || '通用任务'
    },
    orderStatusLabel(value) {
      const labels = { ACCEPTED: '已接单', RUNNING: '执行中', FINISHED: '已完成', COMPLETED: '已完成', CANCELLED: '已取消', ARBITRATION: '仲裁中' }
      return labels[String(value || '').toUpperCase()] || value || '待处理'
    },
    orderStatusClass(value) {
      const normalized = String(value || '').toUpperCase()
      if (['FINISHED', 'COMPLETED'].includes(normalized)) return 'done'
      if (['ACCEPTED', 'RUNNING'].includes(normalized)) return 'running'
      if (normalized === 'CANCELLED') return 'cancelled'
      if (normalized === 'ARBITRATION') return 'warning'
      return 'pending'
    },
    orderLocation(order) {
      if (order.serviceAddress) return order.serviceAddress
      if (order.beginAddress || order.endAddress) return [order.beginAddress, order.endAddress].filter(Boolean).join(' → ')
      return '未记录地点'
    },
    inputDate(value) {
      if (!value) return ''
      const matched = String(value).match(/^\d{4}-\d{2}-\d{2}/)
      if (matched) return matched[0]
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10)
    },
    formatDate(value) {
      if (!value) return '未登记'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value).slice(0, 10)
      return date.toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' })
    },
    formatDateTime(value) {
      if (!value) return '未记录'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return date.toLocaleString('zh-CN', { hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    },
    durationLabel(minutes) {
      const value = Number(minutes || 0)
      if (!value) return '0 分钟'
      if (value < 60) return `${value} 分钟`
      const hours = Math.floor(value / 60)
      const remainder = value % 60
      return remainder ? `${hours} 小时 ${remainder} 分` : `${hours} 小时`
    },
    shortenWallet(value) {
      if (!value) return '未登记'
      const text = String(value)
      return text.length > 20 ? `${text.slice(0, 10)}...${text.slice(-8)}` : text
    },
    number(value) {
      const parsed = Number(value || 0)
      return Number.isFinite(parsed) ? parsed.toFixed(parsed % 1 ? 1 : 0) : '0'
    },
    coin(value) {
      const parsed = Number(value || 0)
      return Number.isFinite(parsed) ? parsed.toFixed(2) : '0.00'
    }
  }
}
</script>

<style scoped>
.archive-page {
  min-height: 100vh;
  color: #172322;
  background: #eef1f2;
  font-family: "Noto Sans", system-ui, sans-serif;
}

.archive-toolbar {
  position: sticky;
  z-index: 20;
  top: 0;
  display: grid;
  grid-template-columns: minmax(150px, 1fr) auto minmax(150px, 1fr);
  align-items: center;
  min-height: 62px;
  padding: 0 32px;
  border-bottom: 1px solid #d4dcdd;
  background: rgba(255, 255, 255, 0.96);
}

.toolbar-button,
.toolbar-actions,
.toolbar-title {
  display: flex;
  align-items: center;
}

.toolbar-button,
.icon-button,
.primary-action,
.secondary-action {
  min-height: 38px;
  border-radius: 6px;
  font: inherit;
  cursor: pointer;
}

.toolbar-button {
  justify-self: start;
  gap: 8px;
  padding: 0 10px;
  border: 0;
  color: #38504d;
  background: transparent;
}

.toolbar-title {
  gap: 9px;
  color: #244b45;
}

.toolbar-title strong { font-size: 16px; }
.toolbar-title span { color: #7c8987; font-size: 12px; font-family: ui-monospace, monospace; }

.toolbar-actions {
  justify-self: end;
  gap: 8px;
}

.icon-button {
  display: grid;
  width: 38px;
  place-items: center;
  border: 1px solid #ccd6d5;
  color: #35534f;
  background: #fff;
}

.primary-action,
.secondary-action {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 0 15px;
  border: 1px solid transparent;
}

.primary-action { color: #fff; background: #25574f; }
.secondary-action { color: #3b514e; border-color: #cbd4d3; background: #fff; }
button:disabled { cursor: not-allowed; opacity: 0.55; }

.archive-main {
  width: min(1380px, calc(100% - 48px));
  margin: 0 auto;
  padding: 34px 0 64px;
}

.identity-band {
  position: relative;
  display: grid;
  grid-template-columns: 152px minmax(0, 1fr) 210px;
  align-items: center;
  gap: 28px;
  min-height: 210px;
  padding: 28px 34px;
  overflow: hidden;
  border-top: 5px solid #25574f;
  border-bottom: 1px solid #d5dcdd;
  background: #fff;
}

.identity-band::after {
  position: absolute;
  right: 36px;
  bottom: -36px;
  width: 180px;
  height: 72px;
  border-top: 2px solid rgba(169, 75, 68, 0.14);
  border-bottom: 2px solid rgba(169, 75, 68, 0.14);
  content: "";
  transform: rotate(-8deg);
}

.robot-portrait {
  position: relative;
  width: 152px;
  height: 152px;
  border: 1px solid #cbd5d4;
  border-radius: 6px;
  padding: 6px;
  background: #f3f5f5;
}

.robot-portrait img,
.portrait-placeholder {
  width: 100%;
  height: 100%;
  border-radius: 3px;
  object-fit: cover;
}

.portrait-placeholder {
  display: grid;
  place-items: center;
  color: #fff;
  background: #315b55;
  font-size: 54px;
  font-weight: 700;
}

.online-indicator {
  position: absolute;
  right: -6px;
  bottom: 12px;
  width: 17px;
  height: 17px;
  border: 4px solid #fff;
  border-radius: 50%;
}

.online-indicator.online,
.status-dot.online { background: #1f9d72; }
.online-indicator.offline,
.status-dot.offline { background: #98a4a2; }

.record-line {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-bottom: 11px;
}

.record-line span {
  padding: 4px 8px;
  border: 1px solid #ccd7d5;
  border-radius: 4px;
  color: #3e5d58;
  background: #f6f8f8;
  font-size: 12px;
}

.identity-copy h1 {
  margin: 0;
  color: #172d2a;
  font-size: 34px;
  line-height: 1.2;
  letter-spacing: 0;
}

.identity-copy > p { margin: 10px 0 18px; color: #687876; }

.identity-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 14px 22px;
  color: #526663;
  font-size: 13px;
}

.identity-meta span { display: inline-flex; align-items: center; gap: 6px; min-width: 0; }

.archive-stamp {
  position: relative;
  z-index: 1;
  display: flex;
  min-height: 110px;
  flex-direction: column;
  justify-content: center;
  padding: 18px;
  border: 2px solid rgba(169, 75, 68, 0.68);
  color: #92433e;
  background: #fffafa;
  transform: rotate(-2deg);
}

.archive-stamp small { font-size: 11px; font-weight: 700; letter-spacing: 0.16em; }
.archive-stamp strong { margin: 7px 0; font-family: ui-monospace, monospace; font-size: 19px; }
.archive-stamp span { font-size: 12px; }

.metrics-band {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  padding: 22px 0;
}

.metric-item {
  min-height: 120px;
  padding: 20px;
  border: 1px solid #d4dcdd;
  border-top: 3px solid #758683;
  border-radius: 6px;
  background: #fff;
}

.metric-item.completed { border-top-color: #2f7c65; }
.metric-item.active { border-top-color: #2e6984; }
.metric-item.income { border-top-color: #b8892b; }
.metric-item.score { border-top-color: #a94b44; }
.metric-item span { display: block; color: #74827f; font-size: 12px; }
.metric-item strong { display: block; margin: 7px 0 2px; color: #1b312e; font-size: 29px; line-height: 1; }
.metric-item strong em { margin-left: 2px; font-size: 14px; font-style: normal; }
.metric-item small { color: #88928f; }

.archive-sheet {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(360px, 0.85fr);
  border: 1px solid #d3dcdd;
  background: #fff;
}

.sheet-column { min-width: 0; padding: 30px 32px; }
.device-column { border-left: 1px solid #dce2e2; background: #f9fafa; }

.section-heading,
.history-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #dce2e2;
}

.section-heading span,
.history-heading span {
  color: #a94b44;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.15em;
}

.section-heading h2,
.history-heading h2 { margin: 3px 0 0; font-size: 20px; letter-spacing: 0; }
.section-heading em { color: #7d8b89; font-family: ui-monospace, monospace; font-size: 12px; font-style: normal; }
.section-heading > .el-icon { color: #3d655f; font-size: 25px; }

.record-grid,
.device-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin: 0;
}

.record-grid > div,
.device-grid > div {
  min-height: 82px;
  padding: 18px 12px 14px 0;
  border-bottom: 1px solid #e4e8e8;
}

.record-grid > div:nth-child(odd),
.device-grid > div:nth-child(odd) { margin-right: 18px; border-right: 1px solid #e4e8e8; }
.record-grid .wide-record { grid-column: 1 / -1; margin-right: 0; border-right: 0; }
dt { margin-bottom: 7px; color: #7a8885; font-size: 11px; }
dd { margin: 0; overflow-wrap: anywhere; color: #253a37; font-weight: 600; }
.mono { font-family: ui-monospace, monospace; font-size: 13px; }

.archive-form { padding-top: 20px; }
.form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 18px; }
.form-grid .full-row { grid-column: 1 / -1; }
.archive-form :deep(.el-date-editor.el-input) { width: 100%; }
.archive-form :deep(.el-input__wrapper),
.archive-form :deep(.el-textarea__inner) { border-radius: 4px; }
.archive-form .field-hint { display: block; margin-top: 7px; color: #6b7a78; font-size: 12px; line-height: 1.55; }

.device-status-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 68px;
  border-bottom: 1px solid #e0e6e5;
}

.status-dot { width: 10px; height: 10px; flex: 0 0 auto; border-radius: 50%; }
.device-status-row div { display: flex; flex-direction: column; gap: 3px; }
.device-status-row small { color: #7a8986; }
.device-grid { margin-bottom: 20px; }
.device-grid dd { font-size: 13px; }

.text-record { padding: 17px 0; border-top: 1px solid #dce2e2; }
.text-record span { color: #7a8885; font-size: 11px; }
.text-record p { margin: 8px 0 0; color: #344a46; line-height: 1.7; white-space: pre-wrap; }
.note-record { border-bottom: 3px double #d4dcdb; }

.history-band {
  margin-top: 22px;
  padding: 30px 32px 34px;
  border: 1px solid #d3dcdd;
  background: #fff;
}

.history-heading { align-items: center; }
.history-heading p { margin: 0; color: #687875; font-size: 13px; text-align: right; }
.history-table { margin-top: 18px; width: 100%; }
.history-table :deep(th.el-table__cell) { color: #5e716d; background: #f3f6f5; font-size: 12px; }
.history-table :deep(.el-table__cell) { padding: 13px 0; }
.order-title { display: flex; flex-direction: column; gap: 5px; }
.order-title small { color: #81908d; }

.order-status {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 8px;
  border-radius: 4px;
  font-size: 12px;
}

.order-status.done { color: #226b55; background: #e5f3ed; }
.order-status.running { color: #245f7d; background: #e4eff5; }
.order-status.cancelled { color: #8b4b47; background: #f5e8e7; }
.order-status.warning { color: #8a651d; background: #f6efdd; }
.order-status.pending { color: #62706e; background: #edf0f0; }
.mobile-history { display: none; }

@media (max-width: 1050px) {
  .identity-band { grid-template-columns: 120px minmax(0, 1fr); }
  .robot-portrait { width: 120px; height: 120px; }
  .archive-stamp { grid-column: 1 / -1; min-height: auto; width: min(300px, 100%); }
  .metrics-band { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .archive-sheet { grid-template-columns: 1fr; }
  .device-column { border-top: 1px solid #dce2e2; border-left: 0; }
}

@media (max-width: 720px) {
  .archive-toolbar { grid-template-columns: auto 1fr; gap: 8px; padding: 8px 14px; }
  .toolbar-title { display: none; }
  .toolbar-actions { min-width: 0; }
  .primary-action span,
  .secondary-action span { display: none; }
  .primary-action,
  .secondary-action { width: 38px; padding: 0; justify-content: center; }
  .archive-main { width: calc(100% - 24px); padding-top: 16px; }
  .identity-band { grid-template-columns: 88px minmax(0, 1fr); gap: 16px; padding: 20px 16px; }
  .robot-portrait { width: 88px; height: 88px; }
  .identity-copy h1 { font-size: 25px; }
  .identity-copy > p { margin-bottom: 12px; }
  .identity-meta { display: grid; gap: 8px; }
  .metrics-band { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .metric-item { min-height: 106px; padding: 16px; }
  .metric-item strong { font-size: 25px; }
  .sheet-column,
  .history-band { padding: 22px 18px; }
  .record-grid,
  .device-grid,
  .form-grid { grid-template-columns: 1fr; }
  .record-grid > div:nth-child(odd),
  .device-grid > div:nth-child(odd) { margin-right: 0; border-right: 0; }
  .record-grid .wide-record,
  .form-grid .full-row { grid-column: auto; }
  .history-heading { align-items: flex-start; flex-direction: column; }
  .history-heading p { text-align: left; }
  .history-table { display: none; }
  .mobile-history { display: grid; gap: 10px; margin-top: 18px; }
  .mobile-history article { padding: 15px; border: 1px solid #dce3e2; border-radius: 6px; }
  .mobile-history article > div { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
  .mobile-history p { margin: 9px 0; color: #657572; }
  .mobile-history small { color: #87928f; }
}

/* Keep the archive layout and record-like hierarchy, while using the
   product-wide dark console palette instead of a separate light theme. */
.archive-page {
  color: var(--roc-text);
  background:
    linear-gradient(rgba(142, 231, 255, 0.028) 1px, transparent 1px),
    linear-gradient(90deg, rgba(142, 231, 255, 0.028) 1px, transparent 1px),
    #050910;
  background-size: 54px 54px;
}

.archive-toolbar {
  border-bottom-color: var(--roc-border);
  background: rgba(5, 12, 22, 0.94);
  backdrop-filter: blur(14px);
}

.toolbar-button,
.toolbar-title,
.identity-copy h1,
.metric-item strong,
.section-heading h2,
.history-heading h2,
dd,
.text-record p { color: var(--roc-text); }

.toolbar-button { color: rgba(226, 237, 248, 0.78); }
.toolbar-title span,
.identity-copy > p,
.identity-meta,
.metric-item span,
.metric-item small,
.section-heading em,
dt,
.archive-form .field-hint,
.device-status-row small,
.text-record span,
.history-heading p,
.order-title small,
.mobile-history p,
.mobile-history small { color: var(--roc-muted); }

.icon-button,
.secondary-action {
  color: rgba(226, 237, 248, 0.88);
  border-color: var(--roc-border);
  background: rgba(255, 255, 255, 0.05);
}

.primary-action {
  color: #051018;
  background: linear-gradient(135deg, #8ee7ff, #74f2bf);
}

.identity-band,
.metric-item,
.archive-sheet,
.history-band {
  border-color: var(--roc-border);
  background: rgba(8, 17, 29, 0.84);
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.16);
}

.identity-band { border-top-color: #74f2bf; }
.device-column { border-left-color: var(--roc-border); background: rgba(3, 10, 19, 0.42); }
.robot-portrait { border-color: var(--roc-border); background: rgba(255, 255, 255, 0.045); }
.online-indicator { border-color: #0b1625; }

.record-line span {
  color: rgba(226, 237, 248, 0.78);
  border-color: var(--roc-border);
  background: rgba(255, 255, 255, 0.05);
}

.archive-stamp {
  border-color: rgba(255, 150, 142, 0.65);
  color: #ffafa8;
  background: rgba(122, 35, 38, 0.15);
}

.metric-item { border-top-color: rgba(142, 231, 255, 0.45); }
.metric-item.completed { border-top-color: #74f2bf; }
.metric-item.active { border-top-color: #8ee7ff; }
.metric-item.income { border-top-color: #f7b85f; }
.metric-item.score { border-top-color: #ff918b; }

.section-heading,
.history-heading,
.record-grid > div,
.device-grid > div,
.device-status-row,
.text-record { border-color: rgba(136, 229, 255, 0.12); }
.record-grid > div:nth-child(odd),
.device-grid > div:nth-child(odd) { border-right-color: rgba(136, 229, 255, 0.12); }
.note-record { border-bottom-color: rgba(136, 229, 255, 0.22); }
.section-heading span,
.history-heading span { color: #ffafa8; }
.section-heading > .el-icon { color: #8ee7ff; }

.history-table :deep(.el-table),
.history-table :deep(.el-table__inner-wrapper),
.history-table :deep(tr),
.history-table :deep(td.el-table__cell) {
  color: rgba(226, 237, 248, 0.88) !important;
  border-color: rgba(136, 229, 255, 0.1) !important;
  background: #0b1625 !important;
}

.history-table :deep(th.el-table__cell) {
  color: #8ee7ff !important;
  background: rgba(142, 231, 255, 0.08) !important;
}

.history-table :deep(.el-table__inner-wrapper::before) { background-color: rgba(136, 229, 255, 0.16) !important; }
.history-table :deep(.el-table--enable-row-hover .el-table__body tr:hover > td.el-table__cell) { background: rgba(142, 231, 255, 0.08) !important; }

.order-status.done { color: #a2f6d2; background: rgba(116, 242, 191, 0.12); }
.order-status.running { color: #a9eaff; background: rgba(142, 231, 255, 0.12); }
.order-status.cancelled { color: #ffb8b2; background: rgba(255, 145, 139, 0.12); }
.order-status.warning { color: #ffdc9d; background: rgba(247, 184, 95, 0.12); }
.order-status.pending { color: rgba(226, 237, 248, 0.72); background: rgba(255, 255, 255, 0.07); }

.archive-form :deep(.el-input__wrapper),
.archive-form :deep(.el-textarea__inner) {
  background: rgba(3, 10, 19, 0.82) !important;
  box-shadow: 0 0 0 1px rgba(136, 229, 255, 0.22) inset !important;
}

.archive-form :deep(.el-input__inner),
.archive-form :deep(.el-textarea__inner),
.archive-form :deep(.el-form-item__label) { color: var(--roc-text) !important; }
.archive-form :deep(.el-input__inner::placeholder),
.archive-form :deep(.el-textarea__inner::placeholder) { color: rgba(226, 237, 248, 0.48) !important; }

@media (max-width: 1050px) {
  .device-column { border-top-color: var(--roc-border); }
}

@media (max-width: 720px) {
  .mobile-history article { border-color: var(--roc-border); background: rgba(255, 255, 255, 0.035); }
}
</style>
